import { useRef, useState } from "react";
import { CheckCircle2, CloudUpload, Upload } from "lucide-react";

import { Button } from "./button";
import { Card, CardContent } from "./card";

type Props = {
    file?: File | null;
    setFile?: (file: File | null) => void;
    accept?: string;
    allowedTypes?: string[];
    maxSizeMB?: number;
};

export default function FileUpload({
    file: externalFile,
    setFile: externalSetFile,
    accept = ".png,.jpg,.jpeg,.pdf,.zip,.csv,.xml",
    allowedTypes = [
        "image/png",
        "image/jpeg",
        "application/pdf",
        "application/zip",
        "text/csv",
        "text/xml",
        "application/xml",
    ],
    maxSizeMB = 50,
}: Props = {}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const MAX_SIZE = maxSizeMB * 1024 * 1024;

    const [localFile, setLocalFile] = useState<File | null>(null);
    const file = externalFile !== undefined ? externalFile : localFile;
    const setFile = externalSetFile || setLocalFile;

    const [isComplete, setIsComplete] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");
    const [dragging, setDragging] = useState(false);

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    const validate = (f: File) => {
        if (allowedTypes && allowedTypes.length > 0 && !allowedTypes.includes(f.type)) {
            const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
            if (!accept.split(',').map(a => a.trim().toLowerCase()).includes(ext)) {
                setError("Please upload supported file type: " + accept.split(',').map(ext => ext.replace('.', '')).join(', '));
                return false;
            }
        }
        if (f.size > MAX_SIZE) {
            setError(`Maximum file size is ${maxSizeMB} MB`);
            return false;
        }
        return true;
    };

    const handleFile = (f?: File) => {
        if (!f) return;

        setError("");

        if (!validate(f)) return;

        setFile(f);
        setUploading(true);
        setProgress(0);

        // simulate upload success (replace with API call)
        const interval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + 10;
            });
        }, 100);

        setTimeout(() => {
            clearInterval(interval);
            setProgress(100);
            setUploading(false);
            setIsComplete(true);
        }, 1100);
    };

    const reset = () => {
        setFile(null);
        setIsComplete(false);
        setUploading(false);
        setProgress(0);
        setError("");

        if (inputRef.current) inputRef.current.value = "";
    };

    return (
        <Card className="w-full max-w-2xl bg-transparent shadow-none">
            <CardContent className="p-4 pt-1 pb-1">

                {/* SUCCESS STATE */}
                {isComplete && file ? (
                    <div className="rounded-xl p-3 text-center">
                        <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-500" />
                        <p className="mt-0 text-sm font-semibold mb-1">{file.name}</p>
                        <p className="text-sm text-muted-foreground m-0 p-0 mb-1">
                            {formatSize(file.size)} · {file.type}
                        </p>
                        <Button
                            size="sm"
                            className="bg-primary text-xs text-primary-foreground hover:bg-primary/90 mt-0.5 h-8 px-3"
                            variant="outline"
                            onClick={reset}
                        >
                            Clear
                        </Button>
                    </div>
                ) : uploading && file ? (
                    /* UPLOADING STATE */
                    <div className="rounded-xl p-2 text-center">
                        <CloudUpload className="mx-auto h-7 w-7 text-primary animate-bounce" />
                        <p className="mt-0.5 font-semibold mb-1">Uploading {file.name}...</p>
                        <p className="mb-2 text-sm text-muted-foreground">{progress}%</p>
                        {/* Progress Bar */}
                        <div className="w-full max-w-sm mx-auto mt-2">
                            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                                <div
                                    className="h-full bg-primary transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    /* UPLOAD AREA */
                    <div
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragging(false);
                            handleFile(e.dataTransfer.files?.[0]);
                        }}
                        className={`rounded-xl p-2 text-center transition ${dragging
                            ? "border-emerald-500 bg-emerald-500/5"
                            : "border-muted"
                            }`}
                    >
                        <CloudUpload className="mx-auto h-7 w-7 text-muted-foreground mb-0" />
                        <p className="mt-0 mb-0.5 text-sm">
                            Drag and drop your file
                        </p>

                        <p className="text-sm text-muted-foreground m-1 mt-0 p-0">
                            or click to browse from your computer
                        </p>

                        <input
                            ref={inputRef}
                            type="file"
                            accept={accept}
                            className="hidden"
                            onChange={(e) => {
                                handleFile(e.target.files?.[0]);
                                e.target.value = "";
                            }}
                        />

                        <Button
                            size="sm"
                            className="bg-primary text-xs text-primary-foreground hover:bg-primary/90 mt-0.5 h-8 px-3"
                            variant="outline"
                            onClick={() => inputRef.current?.click()}
                        >
                            Browse files
                        </Button>
                    </div>
                )}

                {/* FOOTER */}
                {error && (
                    <p className="my-1 text-sm text-red-500">
                        {error}
                    </p>
                )}
                <p className="my-1 text-xs text-muted-foreground">
                    Maximum file size: {maxSizeMB} MB. Supported formats: {accept.split(',').map(ext => ext.replace('.', '')).join(', ')}.
                </p>
            </CardContent>
        </Card>
    );
}